import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Spinner } from "components/ui/BootstrapCompat";
import { useInfiniteQuery, useQuery } from "react-query";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FiAlertTriangle,
  FiChevronRight,
  FiDollarSign,
  FiGift,
  FiRadio,
} from "react-icons/fi";
import { FaGamepad } from "react-icons/fa";
import useLocalStorage from "helpers/useLocalStorage";
import {
  donateDashboardGetDailyAmount,
  donateDashboardGetRecentTransactions,
  donateDashboardGetSummary,
} from "services/main/donate.service";
import {
  getUserProfileData,
  userGetMe,
  userKycGetStatus,
} from "services/user.service";

const PAGE_SIZE = 14;
const TIMEZONE = "Asia/Bangkok";
const DONATE_LINE_COLOR = "#06b6d4";
const TOPUP_LINE_COLOR = "#8b5cf6";

const numberFormatter = new Intl.NumberFormat("lo-LA");
const chartDateKeyFormatter = new Intl.DateTimeFormat("en-US", {
  day: "2-digit",
  month: "2-digit",
  timeZone: TIMEZONE,
  year: "numeric",
});
const chartDayLabelFormatter = new Intl.DateTimeFormat("lo-LA", {
  day: "2-digit",
  month: "short",
  timeZone: TIMEZONE,
});

const toNumber = (value: unknown) => {
  if (value === undefined || value === null || value === "") return 0;
  const normalized =
    typeof value === "string" ? value.replace(/,/g, "") : value;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : 0;
};

const getFirstNumber = (source: any, keys: string[]) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value === undefined || value === null || value === "") continue;
    return toNumber(value);
  }

  return undefined;
};

const formatCurrency = (amount: number | string) =>
  `₭ ${numberFormatter.format(toNumber(amount))}`;

const formatCount = (amount: number | string) =>
  numberFormatter.format(toNumber(amount));

const formatCompactCurrency = (amount: number | string) => {
  const value = toNumber(amount);
  const absoluteValue = Math.abs(value);

  if (absoluteValue >= 1_000_000_000)
    return `${numberFormatter.format(Math.round(value / 1_000_000_000))}B`;
  if (absoluteValue >= 1_000_000)
    return `${numberFormatter.format(Math.round(value / 1_000_000))}M`;
  if (absoluteValue >= 1_000)
    return `${numberFormatter.format(Math.round(value / 1_000))}K`;

  return numberFormatter.format(value);
};

const getDailyDateValue = (item: any) =>
  item?.date ||
  item?.day ||
  item?.createdAt ||
  item?.orderedAt ||
  item?.completedAt ||
  item?.updatedAt ||
  "";

const getDailyDateKey = (value?: string) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) return rawValue.slice(0, 10);

  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) return rawValue;

  const parts = chartDateKeyFormatter
    .formatToParts(date)
    .reduce<Record<string, string>>((result, part) => {
      result[part.type] = part.value;
      return result;
    }, {});

  return `${parts.year}-${parts.month}-${parts.day}`;
};

const formatDailyDateLabel = (value?: string) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "--";

  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) return rawValue;

  return chartDayLabelFormatter.format(date);
};

const getDailySortValue = (value?: string) => {
  const date = new Date(String(value || ""));
  if (!Number.isNaN(date.getTime())) return date.getTime();

  const fallbackDate = new Date(getDailyDateKey(value));
  if (!Number.isNaN(fallbackDate.getTime())) return fallbackDate.getTime();

  return Number.MAX_SAFE_INTEGER;
};

const formatDateTime = (value?: string) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleString("lo-LA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: TIMEZONE,
  });
};

const formatTime = (value?: string) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleTimeString("lo-LA", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: TIMEZONE,
  });
};

const formatDisplayValue = (value: any) => {
  if (value === undefined || value === null || value === "") return "--";
  if (Array.isArray(value)) {
    const listValue = value.filter(Boolean).join(", ");
    return listValue || "--";
  }
  if (typeof value === "object") {
    return (
      value?.message ||
      value?.description ||
      value?.name ||
      JSON.stringify(value)
    );
  }

  return String(value);
};

const normalizeList = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.rows)) return response.rows;
  if (Array.isArray(response?.list)) return response.list;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.data?.rows)) return response.data.rows;
  if (Array.isArray(response?.data?.list)) return response.data.list;
  if (Array.isArray(response?.data?.results)) return response.data.results;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data?.data?.items))
    return response.data.data.items;
  if (Array.isArray(response?.data?.data?.rows)) return response.data.data.rows;
  if (Array.isArray(response?.data?.data?.list)) return response.data.data.list;
  return [];
};

const getRecentTransactionTotal = (response: any) =>
  getFirstNumber(response, ["count", "total", "totalCount"]) ??
  getFirstNumber(response?.data, ["count", "total", "totalCount"]) ??
  getFirstNumber(response?.data?.data, ["count", "total", "totalCount"]);

const getTransactionType = (transaction: any) =>
  String(
    transaction?.type ||
      transaction?.transactionType ||
      transaction?.category ||
      "",
  ).toUpperCase();

const getTransactionAmount = (transaction: any) =>
  toNumber(
    transaction?.total ??
      transaction?.amount ??
      transaction?.totalAmount ??
      transaction?.transactionAmount ??
      transaction?.paymentAmount ??
      transaction?.orders?.[0]?.total ??
      transaction?.orders?.[0]?.amount ??
      0,
  );

const getDonorName = (transaction: any) =>
  transaction?.donorName ||
  transaction?.name ||
  transaction?.userName ||
  transaction?.customerName ||
  transaction?.senderName ||
  transaction?.orders?.[0]?.donorName ||
  transaction?.orders?.[0]?.userName ||
  transaction?.orders?.[0]?.customerName ||
  transaction?.orders?.[0]?.productAgentName ||
  "--";

const getTransactionMessage = (transaction: any) =>
  formatDisplayValue(
    transaction?.message ||
      transaction?.description ||
      transaction?.note ||
      transaction?.payload ||
      transaction?.orders?.[0]?.message ||
      transaction?.orders?.[0]?.description ||
      transaction?.orders?.[0]?.productAgentPackageRef?.packageGame,
  );

const getPaymentMethod = (transaction: any) => {
  if (typeof transaction?.payment === "string") return transaction.payment;

  return (
    transaction?.paymentMethod ||
    transaction?.paymentChannel ||
    transaction?.payment?.name ||
    transaction?.payment?.type ||
    transaction?.gateway ||
    transaction?.method ||
    transaction?.bankName ||
    transaction?.orders?.[0]?.paymentMethod ||
    transaction?.orders?.[0]?.paymentChannel ||
    (getTransactionType(transaction).includes("TOPUP") ? "Termgame" : "Donate")
  );
};

const getCreatedAt = (transaction: any) =>
  transaction?.createdAt ||
  transaction?.orderedAt ||
  transaction?.completedAt ||
  transaction?.updatedAt;

const getStatusLabel = (status?: string) => {
  switch (String(status || "").toUpperCase()) {
    case "SUCCESS":
      return "ສຳເລັດ";
    case "IN_QUEUE":
      return "ໃນຄິວ";
    case "CREATED":
      return "ສ້າງແລ້ວ";
    case "FAILED":
      return "ລົ້ມເຫລວ";
    default:
      return status || "--";
  }
};

const getStatusClassName = (status?: string) => {
  if (String(status || "").toUpperCase() === "SUCCESS") {
    return "bg-emerald-50 text-emerald-700";
  }

  if (String(status || "").toUpperCase() === "FAILED") {
    return "bg-rose-50 text-rose-700";
  }

  return "bg-amber-50 text-amber-700";
};

const getTransactionId = (transaction: any) =>
  transaction?._id ||
  transaction?.id ||
  transaction?.donationId ||
  transaction?.transactionId ||
  transaction?.orderId ||
  transaction?.orders?.[0]?._id ||
  transaction?.orders?.[0]?.id ||
  "";

const getTransactionTypeLabel = (transaction: any) => {
  const type = getTransactionType(transaction);

  if (type.includes("TOPUP") || type.includes("TERMGAME")) return "Game";
  if (type.includes("DONATE") || type.includes("DONATION")) return "Donate";

  return type || "Transaction";
};

const getTransactionSignedAmount = (transaction: any) => {
  const amount = getTransactionAmount(transaction);
  const type = getTransactionType(transaction);

  if (amount < 0 || type.includes("WITHDRAW")) return -Math.abs(amount);
  return amount;
};

const getTransactionVisual = (transaction: any) => {
  const type = getTransactionType(transaction);

  if (type.includes("TOPUP") || type.includes("TERMGAME")) {
    return {
      Icon: FaGamepad,
      label: "Game",
      className:
        "border-violet-100 bg-violet-50 text-violet-600 dark:border-violet-900/50 dark:bg-violet-950/40 dark:text-violet-300",
    };
  }

  if (type.includes("DONATE") || type.includes("DONATION")) {
    return {
      Icon: FiGift,
      label: "Donate",
      className:
        "border-cyan-100 bg-cyan-50 text-cyan-600 dark:border-cyan-900/50 dark:bg-cyan-950/40 dark:text-cyan-300",
    };
  }

  return {
    Icon: FiDollarSign,
    label: "Transaction",
    className:
      "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
  };
};

const TransactionDetailField = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) => (
  <div className="rounded-lg border border-[#dbe4ef] bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
    <div className="text-[11px] font-bold uppercase text-[#667085] dark:text-slate-400">
      {label}
    </div>
    <div
      className={`mt-1 break-words text-[13px] font-black text-[#344054] dark:text-slate-100 ${
        mono ? "font-mono" : ""
      }`}
    >
      {value === undefined || value === null || value === "" ? "--" : value}
    </div>
  </div>
);

export default function DashboardPage() {
  const navigate = useNavigate();
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");
  const [clock, setClock] = React.useState(() => new Date());
  const [selectedTransaction, setSelectedTransaction] =
    React.useState<any>(null);
  const isReady = !!token && !!businessId;
  const queryBase = { token: token || "", businessId: businessId || "" };

  React.useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const { data: summary } = useQuery(
    ["dash-summary", businessId],
    async () => {
      try {
        return await donateDashboardGetSummary({
          ...queryBase,
          timezone: TIMEZONE,
        });
      } catch {
        return {
          total: 0,
          success: 0,
          inQueue: 0,
          created: 0,
          failed: 0,
        };
      }
    },
    { enabled: isReady },
  );

  const { data: dailyAmount, isLoading: isLoadingDailyAmount } = useQuery(
    ["dash-daily-amount", businessId],
    async () => {
      try {
        return await donateDashboardGetDailyAmount({
          ...queryBase,
          timezone: TIMEZONE,
        });
      } catch {
        return [];
      }
    },
    { enabled: isReady },
  );

  const {
    data: recentTransactionsPages,
    isLoading: isLoadingTransactions,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ["dash-recent-transactions", businessId],
    async ({ pageParam = 0 }) => {
      const skip = Math.max(Number(pageParam) || 0, 0);

      try {
        return await donateDashboardGetRecentTransactions({
          ...queryBase,
          skip,
          limit: PAGE_SIZE,
          sortBy: "createdAt",
          order: "desc",
          timezone: TIMEZONE,
          status: "SUCCESS",
        });
      } catch {
        return {
          data: [],
          count: 0,
          skip,
          limit: PAGE_SIZE,
        };
      }
    },
    {
      enabled: isReady,
      getNextPageParam: (lastPage, allPages) => {
        const rows = normalizeList(lastPage);
        const loadedCount = allPages.reduce(
          (total, page) => total + normalizeList(page).length,
          0,
        );
        const totalCount = getRecentTransactionTotal(lastPage);

        if (typeof totalCount === "number" && loadedCount >= totalCount) {
          return undefined;
        }

        if (rows.length < PAGE_SIZE) return undefined;

        return loadedCount;
      },
    },
  );

  const { data: authMeResponse } = useQuery(
    ["dashboard-auth-me", businessId],
    () => userGetMe({ token: token || "", businessId: businessId || "" }),
    { enabled: Boolean(token), retry: false },
  );

  const { data: kycStatusResponse } = useQuery(
    ["dashboard-kyc-status", businessId],
    () =>
      userKycGetStatus({ token: token || "", businessId: businessId || "" }),
    { enabled: isReady, retry: false },
  );

  const transactions = React.useMemo(
    () =>
      (recentTransactionsPages?.pages || []).flatMap((page) =>
        normalizeList(page),
      ),
    [recentTransactionsPages],
  );
  const transactionLoadMoreRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const element = transactionLoadMoreRef.current;
    if (
      !element ||
      !hasNextPage ||
      isFetchingNextPage ||
      isLoadingTransactions
    ) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      { rootMargin: "220px 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoadingTransactions,
    transactions.length,
  ]);

  const totals = React.useMemo(() => {
    const dailyRows = normalizeList(dailyAmount);
    const dailyDonateAmount = dailyRows.reduce(
      (sum: number, item: any) =>
        sum +
        toNumber(
          item?.donateAmount ??
            item?.donationAmount ??
            item?.donateTotal ??
            item?.donationTotal ??
            0,
        ),
      0,
    );
    const dailyTopupAmount = dailyRows.reduce(
      (sum: number, item: any) =>
        sum +
        toNumber(
          item?.topupAmount ??
            item?.termgameAmount ??
            item?.gameAmount ??
            item?.topupTotal ??
            0,
        ),
      0,
    );

    const summaryDonateAmount = getFirstNumber(summary, [
      "donateAmount",
      "donationAmount",
      "donateTotal",
      "donationTotal",
      "totalDonationAmount",
    ]);
    const summaryTopupAmount = getFirstNumber(summary, [
      "topupAmount",
      "termgameAmount",
      "gameAmount",
      "topupTotal",
      "totalTopupAmount",
    ]);
    const donateAmount = summaryDonateAmount ?? dailyDonateAmount;
    const topupAmount = summaryTopupAmount ?? dailyTopupAmount;
    const summaryTotalAmount = getFirstNumber(summary, [
      "totalAmount",
      "revenue",
      "totalRevenue",
      "grossAmount",
      "amount",
    ]);

    return {
      donateAmount,
      topupAmount,
      totalAmount: summaryTotalAmount ?? donateAmount + topupAmount,
    };
  }, [dailyAmount, summary]);

  const dailyRevenueChartData = React.useMemo(() => {
    const dailyRows = normalizeList(dailyAmount);
    const dailyMap = new Map<
      string,
      {
        date: string;
        donateAmount: number;
        topupAmount: number;
        sortValue: number;
      }
    >();

    dailyRows.forEach((item: any) => {
      const dateValue = getDailyDateValue(item);
      const dateKey = getDailyDateKey(dateValue);
      if (!dateKey) return;

      const current = dailyMap.get(dateKey) || {
        date: formatDailyDateLabel(dateValue),
        donateAmount: 0,
        topupAmount: 0,
        sortValue: getDailySortValue(dateValue),
      };

      current.donateAmount +=
        getFirstNumber(item, [
          "donateAmount",
          "donationAmount",
          "donateTotal",
          "donationTotal",
          "totalDonationAmount",
        ]) ?? 0;
      current.topupAmount +=
        getFirstNumber(item, [
          "topupAmount",
          "termgameAmount",
          "gameAmount",
          "topupTotal",
          "totalTopupAmount",
        ]) ?? 0;

      dailyMap.set(dateKey, current);
    });

    return Array.from(dailyMap.values())
      .sort((a, b) => a.sortValue - b.sortValue)
      .map(({ sortValue, ...item }) => item);
  }, [dailyAmount]);

  const counts = React.useMemo(() => {
    const donationCount =
      getFirstNumber(summary, ["donationCount", "donateCount", "donations"]) ??
      transactions.filter((item) => getTransactionType(item).includes("DONATE"))
        .length;
    const topupCount =
      getFirstNumber(summary, ["topupCount", "termgameCount", "gameCount"]) ??
      transactions.filter((item) => getTransactionType(item).includes("TOPUP"))
        .length;

    return {
      total:
        getFirstNumber(summary, ["total", "count", "totalCount"]) ??
        transactions.length,
      success: getFirstNumber(summary, ["success", "successCount"]) ?? 0,
      pending:
        (getFirstNumber(summary, ["inQueue", "queueCount"]) ?? 0) +
        (getFirstNumber(summary, ["created", "createdCount"]) ?? 0),
      donationCount,
      topupCount,
    };
  }, [summary, transactions]);

  const topSupporters = React.useMemo(() => {
    const supporters = new Map<string, number>();

    transactions.forEach((transaction) => {
      const donorName = getDonorName(transaction);
      if (!donorName || donorName === "--") return;
      supporters.set(
        donorName,
        (supporters.get(donorName) || 0) + getTransactionAmount(transaction),
      );
    });

    return Array.from(supporters.entries())
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }, [transactions]);

  const authMeData = getUserProfileData(authMeResponse);
  const kycStatus = String(
    kycStatusResponse?.data?.status ||
      (typeof kycStatusResponse?.data === "string"
        ? kycStatusResponse.data
        : "") ||
      kycStatusResponse?.status ||
      authMeData?.kycStatus ||
      "draft",
  ).toLowerCase();
  const isKycApproved = kycStatus === "approved";
  const alertHtml = authMeData?.widgets?.donationAlert?.html;
  const isAlertReady = Boolean(alertHtml || authMeData?.donationAlertEnabled);
  const merchantName =
    authMeData?.fullName ||
    authMeData?.merchantName ||
    authMeData?.username ||
    "Streamer";
  const clockLabel = clock.toLocaleTimeString("lo-LA", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: TIMEZONE,
  });

  const summaryCards = [
    // {
    //   title: "ລາຍຮັບທັງໝົດ",
    //   value: formatCurrency(totals.totalAmount),
    //   note: "Donate ແລະ ເຕີມເກມ",
    //   icon: FiDollarSign,
    //   accentClass: "from-emerald-400 to-teal-500",
    //   surfaceClass: "from-emerald-50/90 via-white to-white",
    //   glowClass: "bg-emerald-300/20",
    //   iconClass: "text-emerald-400",
    //   noteClass: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    // },
    {
      title: "ລາຍຮັບຈາກການບໍລິຈາກ",
      value: formatCurrency(totals.donateAmount),
      note: `${formatCount(counts.donationCount)} ຄັ້ງ`,
      icon: FiGift,
      accentClass: "from-cyan-400 to-sky-500",
      surfaceClass: "from-cyan-50/90 via-white to-white",
      glowClass: "bg-cyan-300/25",
      iconClass: "text-cyan-400",
      noteClass: "bg-cyan-50 text-cyan-700 ring-cyan-100",
    },
    {
      title: "ລາຍຮັບເຕີມເກມ",
      value: formatCurrency(totals.topupAmount),
      note: `${formatCount(counts.topupCount)} ຄັ້ງ`,
      icon: FiRadio,
      accentClass: "from-violet-400 to-fuchsia-500",
      surfaceClass: "from-violet-50/90 via-white to-white",
      glowClass: "bg-violet-300/25",
      iconClass: "text-violet-400",
      noteClass: "bg-violet-50 text-violet-700 ring-violet-100",
    },
  ];

  const selectedTransactionVisual = selectedTransaction
    ? getTransactionVisual(selectedTransaction)
    : null;
  const SelectedTransactionIcon =
    selectedTransactionVisual?.Icon || FiDollarSign;

  const renderTransactionListItem = (transaction: any, index: number) => {
    const transactionId = getTransactionId(transaction);
    const createdAt = getCreatedAt(transaction);
    const signedAmount = getTransactionSignedAmount(transaction);
    const isDebit = signedAmount < 0;
    const visual = getTransactionVisual(transaction);
    const Icon = visual.Icon;

    return (
      <button
        type="button"
        key={transactionId || index}
        className="group flex w-full min-w-0 items-center gap-3 rounded-lg border border-[#dbe4ef] bg-white px-3 py-2.5 text-left shadow-[0_8px_20px_rgba(16,24,40,0.05)] transition hover:border-primary hover:bg-[#f8fbff] focus:outline-none focus:ring-2 focus:ring-primary/30 md:px-4 md:py-3 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
        onClick={() => setSelectedTransaction(transaction)}
        aria-label={`Open ${visual.label} transaction details`}
      >
        <span
          className={`flex h-10 w-10 flex-none items-center justify-center rounded-lg border ${visual.className}`}
          title={visual.label}
        >
          <Icon className="h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-black text-[#344054] md:hidden dark:text-slate-100">
            {formatTime(createdAt)}
          </span>

          <div className="hidden min-w-0 md:block">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">
                {getDonorName(transaction)}
              </span>
              <span
                className={`inline-flex flex-none rounded-full px-2.5 py-1 text-[11px] font-semibold ${getStatusClassName(
                  transaction?.status,
                )}`}
              >
                {getStatusLabel(transaction?.status)}
              </span>
            </div>
            <div className="mt-1 flex min-w-0 items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span className="truncate">
                {getTransactionMessage(transaction)}
              </span>
              <span className="h-1 w-1 flex-none rounded-full bg-slate-300" />
              <span className="flex-none">{formatDateTime(createdAt)}</span>
            </div>
          </div>
        </div>

        <span
          className={`max-w-[46%] flex-none truncate text-right text-[15px] font-black leading-tight md:max-w-[220px] md:text-base ${
            isDebit ? "text-red-600" : "text-emerald-600"
          }`}
          title={`${isDebit ? "-" : "+"}${formatCurrency(
            Math.abs(getTransactionAmount(transaction)),
          )}`}
        >
          {isDebit ? "-" : "+"}
          {formatCurrency(Math.abs(getTransactionAmount(transaction)))}
        </span>
      </button>
    );
  };

  return (
    <div className="min-h-full min-w-0 bg-slate-50 text-slate-900">
      <div className="flex w-full flex-col gap-4">
        {!isKycApproved ? (
          <section className="flex items-center gap-2 text-sm text-amber-600">
            <FiAlertTriangle className="h-4 w-4" />
            <span>ຕັ້ງຄ່າບັນຊີໃຫ້ພ້ອມກ່ອນເລີ່ມໃຊ້ງານ</span>
          </section>
        ) : null}

        <section className="grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-4">
          {summaryCards.map((card) => {
            const Icon = card.icon;

            return (
              <div
                key={card.title}
                className={`group relative overflow-hidden rounded-lg border border-white/80 bg-gradient-to-br ${card.surfaceClass} p-3 shadow-[0_14px_34px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/70 transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_42px_rgba(15,23,42,0.12)] sm:p-5`}
              >
                <div
                  className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${card.accentClass}`}
                />
                <Icon
                  className={`pointer-events-none absolute -right-2 top-8 h-24 w-24 opacity-[0.13] transition duration-200 group-hover:scale-105 group-hover:opacity-[0.18] sm:-right-3 sm:top-7 sm:h-32 sm:w-32 ${card.iconClass}`}
                />
                <div className="relative flex min-h-[132px] flex-col">
                  <div className="mt-2 min-h-[34px] text-[18px] font-semibold leading-tight text-slate-500 sm:min-h-0">
                    {card.title}
                  </div>
                  <div className="mt-2 break-words text-[28px] font-bold leading-tight text-slate-950 sm:text-3xl">
                    {card.value}
                  </div>
                  <div className="mt-auto pt-4">
                    <span
                      className={`inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-[11px] font-semibold leading-tight ring-1 sm:text-xs ${card.noteClass}`}
                    >
                      {card.note}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        <section className="grid gap-4 xl:grid-cols-[1.05fr_1fr]">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="m-0 text-base font-semibold text-slate-900">
                  ລາຍຮັບລາຍວັນ
                </h2>
                <p className="mb-0 mt-1 text-xs text-slate-500">
                  ປຽບທຽບ Donate ແລະ ເຕີມເກມ
                </p>
              </div>
              <FiDollarSign className="h-5 w-5 text-slate-400" />
            </div>

            <div className="mt-4 h-[280px]">
              {isLoadingDailyAmount ? (
                <div className="flex h-full items-center justify-center gap-2 rounded-lg bg-slate-50 text-sm text-slate-500">
                  <Spinner animation="border" size="sm" />
                  <span>ກຳລັງໂຫລດ...</span>
                </div>
              ) : dailyRevenueChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={dailyRevenueChartData}
                    margin={{ bottom: 0, left: 0, right: 10, top: 8 }}
                  >
                    <CartesianGrid
                      stroke="#e2e8f0"
                      strokeDasharray="3 3"
                      vertical={false}
                    />
                    <XAxis
                      axisLine={{ stroke: "#e2e8f0" }}
                      dataKey="date"
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      tickLine={false}
                    />
                    <YAxis
                      axisLine={false}
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      tickFormatter={(value) => formatCompactCurrency(value)}
                      tickLine={false}
                      width={48}
                    />
                    <RechartsTooltip
                      formatter={(value: any, name: any) => [
                        formatCurrency(Number(value)),
                        name,
                      ]}
                      labelFormatter={(label) => `ວັນທີ ${label}`}
                    />
                    <Line
                      activeDot={{ r: 5 }}
                      dataKey="donateAmount"
                      dot={{ r: 3 }}
                      name="Donate"
                      stroke={DONATE_LINE_COLOR}
                      strokeWidth={3}
                      type="linear"
                    />
                    <Line
                      activeDot={{ r: 5 }}
                      dataKey="topupAmount"
                      dot={{ r: 3 }}
                      name="ເຕີມເກມ"
                      stroke={TOPUP_LINE_COLOR}
                      strokeWidth={3}
                      type="linear"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center rounded-lg bg-slate-50 text-sm text-slate-400">
                  ຂໍ້ມູນຈະສະແດງຫຼັງມີລາຍຮັບລາຍວັນ
                </div>
              )}
            </div>

            <div className="mt-4 grid gap-x-6 gap-y-3 border-t border-slate-100 pt-4 sm:grid-cols-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-cyan-700">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: DONATE_LINE_COLOR }}
                  />
                  Donate
                </div>
                <div className="min-w-0 break-words text-right text-base font-bold text-slate-900">
                  {formatCurrency(totals.donateAmount)}
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-violet-700">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: TOPUP_LINE_COLOR }}
                  />
                  ເຕີມເກມ
                </div>
                <div className="min-w-0 break-words text-right text-base font-bold text-slate-900">
                  {formatCurrency(totals.topupAmount)}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="m-0 text-base font-semibold text-slate-900">
                  ອັນດັບຜູ້ສະໜັບສະໜູນ
                </h2>
                <p className="mb-0 mt-1 text-xs text-slate-500">
                  ລວມຍອດຈາກລາຍການຫຼ້າສຸດ
                </p>
              </div>
              <button
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-900"
                type="button"
                onClick={() => navigate("/transaction")}
                aria-label="Open transactions"
              >
                <FiChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {topSupporters.length > 0 ? (
                topSupporters.map((supporter, index) => (
                  <div
                    key={supporter.name}
                    className="grid grid-cols-[34px_1fr_auto] items-center gap-3"
                  >
                    <div
                      className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                        index === 0
                          ? "bg-amber-100 text-amber-700"
                          : index === 1
                            ? "bg-slate-100 text-slate-600"
                            : index === 2
                              ? "bg-rose-100 text-rose-600"
                              : "bg-sky-50 text-sky-600"
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div className="min-w-0 truncate text-sm font-medium text-slate-800">
                      {supporter.name}
                    </div>
                    <div className="text-sm font-semibold text-slate-900">
                      {formatCurrency(supporter.amount)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex h-[190px] items-center justify-center rounded-lg bg-slate-50 text-sm text-slate-400">
                  ຍັງບໍ່ມີຜູ້ສະໜັບສະໜູນ
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="space-y-2">
          {isLoadingTransactions ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }, (_, index) => (
                <div
                  key={`dashboard-transaction-skeleton-${index}`}
                  className="flex items-center gap-3 rounded-lg border border-[#dbe4ef] bg-white px-3 py-2.5 shadow-[0_8px_20px_rgba(16,24,40,0.05)] md:px-4 md:py-3 dark:border-slate-700 dark:bg-slate-900"
                >
                  <div className="h-10 w-10 flex-none animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="h-3 w-16 animate-pulse rounded bg-slate-100 md:w-36 dark:bg-slate-700" />
                    <div className="hidden h-3 w-56 animate-pulse rounded bg-slate-100 md:block dark:bg-slate-700" />
                  </div>
                  <div className="h-4 w-24 flex-none animate-pulse rounded bg-slate-100 dark:bg-slate-700" />
                </div>
              ))}
            </div>
          ) : transactions.length > 0 ? (
            <>
              {transactions.map(renderTransactionListItem)}
              <div
                ref={transactionLoadMoreRef}
                className="flex min-h-[52px] items-center justify-center text-center text-xs font-medium text-slate-500 dark:text-slate-400"
              >
                {isFetchingNextPage ? (
                  <span className="inline-flex items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    ກຳລັງໂຫລດເພີ່ມ...
                  </span>
                ) : hasNextPage ? (
                  <button
                    type="button"
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 font-semibold text-slate-600 shadow-sm"
                    onClick={() => fetchNextPage()}
                  >
                    ໂຫລດເພີ່ມ
                  </button>
                ) : (
                  "ສະແດງລາຍການຄົບແລ້ວ"
                )}
              </div>
            </>
          ) : (
            <div className="flex h-[220px] items-center justify-center rounded-lg border border-dashed border-[#dbe4ef] bg-white px-4 text-center text-sm text-slate-400 md:h-[260px] dark:border-slate-700 dark:bg-slate-900">
              ຍັງບໍ່ມີລາຍການ
            </div>
          )}
        </div>

        {selectedTransaction && selectedTransactionVisual ? (
          <Modal
            show={!!selectedTransaction}
            onHide={() => setSelectedTransaction(null)}
            centered
            className="transaction-detail-modal-backdrop"
            dialogClassName="transaction-detail-modal-dialog"
          >
            <Modal.Header closeButton>
              <Modal.Title>Transaction Detail</Modal.Title>
            </Modal.Header>
            <Modal.Body className="transaction-detail-modal-body max-h-[70vh] overflow-y-auto">
              <div className="rounded-lg border border-[#dbe4ef] bg-[#f8fafc] p-3 dark:border-slate-700 dark:bg-slate-800">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span
                      className={`flex h-10 w-10 flex-none items-center justify-center rounded-lg border ${selectedTransactionVisual.className}`}
                    >
                      <SelectedTransactionIcon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-200">
                          {getTransactionTypeLabel(selectedTransaction)}
                        </span>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${getStatusClassName(
                            selectedTransaction?.status,
                          )}`}
                        >
                          {getStatusLabel(selectedTransaction?.status)}
                        </span>
                      </div>
                      <div className="text-[12px] font-bold text-[#667085] dark:text-slate-400">
                        {formatDateTime(getCreatedAt(selectedTransaction))}
                      </div>
                    </div>
                  </div>
                  <div className="flex-none text-right">
                    <div
                      className={`text-xl font-black leading-tight ${
                        getTransactionSignedAmount(selectedTransaction) < 0
                          ? "text-red-600"
                          : "text-emerald-600"
                      }`}
                    >
                      {getTransactionSignedAmount(selectedTransaction) < 0
                        ? "-"
                        : "+"}
                      {formatCurrency(
                        Math.abs(getTransactionAmount(selectedTransaction)),
                      )}
                    </div>
                    <div className="text-[11px] font-bold text-[#667085] dark:text-slate-400">
                      {getPaymentMethod(selectedTransaction)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 grid gap-2">
                <TransactionDetailField
                  label="ID"
                  value={getTransactionId(selectedTransaction) || "--"}
                  mono
                />
                <TransactionDetailField
                  label="Type"
                  value={getTransactionTypeLabel(selectedTransaction)}
                />
                <TransactionDetailField
                  label="Donor"
                  value={getDonorName(selectedTransaction)}
                />
                <TransactionDetailField
                  label="Message"
                  value={getTransactionMessage(selectedTransaction)}
                />
                <div className="grid grid-cols-2 gap-2">
                  <TransactionDetailField
                    label="Payment"
                    value={getPaymentMethod(selectedTransaction)}
                  />
                  <TransactionDetailField
                    label="Status"
                    value={getStatusLabel(selectedTransaction?.status)}
                  />
                </div>
                <TransactionDetailField
                  label="Date"
                  value={formatDateTime(getCreatedAt(selectedTransaction))}
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setSelectedTransaction(null)}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        ) : null}
      </div>
    </div>
  );
}
